import React, {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Arrow, MediaDevices } from 'icons/untitledui';
import { useDeleteShiftNoteAttachmentMutation } from 'shared/hooks/api/shiftNotes';
import isVideoAttachment from 'shiftNotes/helpers/isVideoAttachment';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';

import ConfirmDialog from 'shiftNotes/components/ConfirmDialog';
import AttachmentModal from 'shiftNotes/components/NotesStack/AttachmentModal';
import * as Styled from 'shiftNotes/styles/NotesStack.style';
import type { Attachment } from 'shiftNotes/_types';

import CctvCameraIcon from 'shiftNotes/assets/cctvCamera.svg?react';

interface NoteAttachmentsProps {
  noteId: string;
  attachments: Attachment[];
  isFromCamera?: boolean;
  canDelete?: boolean;
}

type ScrollDirection = 'previous' | 'next';

const TILE_ALIGNMENT_TOLERANCE = 0.05;

const getGalleryScrollStep = (gallery: HTMLDivElement): number => {
  const firstTile = gallery.firstElementChild as HTMLElement | null;
  if (!firstTile) return gallery.clientWidth;
  const gap = parseFloat(getComputedStyle(gallery).columnGap) || 0;
  return firstTile.offsetWidth + gap;
};

export default function NoteAttachments({
  noteId,
  attachments,
  isFromCamera = false,
  canDelete = false,
}: NoteAttachmentsProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');
  const capture = usePosthogCapture();
  const [selectedAttachment, setSelectedAttachment] =
    useState<Attachment | null>(null);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] =
    useState<Attachment | null>(null);
  const { mutate: deleteAttachment } =
    useDeleteShiftNoteAttachmentMutation(noteId);
  const galleryRef = useRef<HTMLDivElement>(null);
  const [canScrollPrevious, setCanScrollPrevious] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [settledAttachmentIds, setSettledAttachmentIds] = useState<Set<string>>(
    () => new Set()
  );

  const updateScrollButtons = useCallback(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;
    const maxScrollLeft = gallery.scrollWidth - gallery.clientWidth;
    setCanScrollPrevious(gallery.scrollLeft > 1);
    setCanScrollNext(gallery.scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) return undefined;
    updateScrollButtons();
    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(gallery);
    return () => observer.disconnect();
  }, [attachments.length, updateScrollButtons]);

  if (attachments.length === 0) return null;

  const isSingle = attachments.length === 1;

  const openAttachment = (attachment: Attachment) => {
    setSelectedAttachment(attachment);
    setIsAttachmentModalOpen(true);
    capture.noteAttachmentOpened({ shift_note_id: noteId });
  };

  const handleTileClick =
    (attachment: Attachment) => (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      openAttachment(attachment);
    };

  const handleTileKeyDown =
    (attachment: Attachment) => (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      openAttachment(attachment);
    };

  const handleScrollClick = (direction: ScrollDirection) => () => {
    const gallery = galleryRef.current;
    if (!gallery) return;
    const step = getGalleryScrollStep(gallery);
    const currentTile = gallery.scrollLeft / step;
    const targetTile =
      direction === 'next'
        ? Math.floor(currentTile + TILE_ALIGNMENT_TOLERANCE) + 1
        : Math.ceil(currentTile - TILE_ALIGNMENT_TOLERANCE) - 1;
    gallery.scrollTo({
      left: Math.max(0, targetTile * step),
      behavior: 'smooth',
    });
    capture.noteAttachmentsScrolled({ shift_note_id: noteId, direction });
  };

  const handleConfirmDelete = () => {
    if (!attachmentToDelete) return;
    deleteAttachment(attachmentToDelete.id);
    capture.noteAttachmentDeleted({ shift_note_id: noteId });
    setIsAttachmentModalOpen(false);
    setAttachmentToDelete(null);
  };

  const renderTile = (attachment: Attachment) => {
    const isLoading = !settledAttachmentIds.has(attachment.id);
    const handleSettled = () =>
      setSettledAttachmentIds((previous) =>
        new Set(previous).add(attachment.id)
      );

    const isVideo = isVideoAttachment(attachment);

    return (
      <Styled.AttachmentTile
        key={attachment.id}
        $isSingle={isSingle}
        $isLoading={isLoading}
        onClick={handleTileClick(attachment)}
        onKeyDown={handleTileKeyDown(attachment)}
        role="button"
        tabIndex={0}
        aria-label={t('note.openAttachment')}
        aria-busy={isLoading}
      >
        {isVideo ? (
          <Styled.AttachmentVideo
            src={`${attachment.file}#t=0.001`}
            muted
            playsInline
            preload="metadata"
            $isLoading={isLoading}
            onLoadedData={handleSettled}
            onError={handleSettled}
          />
        ) : (
          <Styled.AttachmentImage
            src={attachment.file}
            alt=""
            loading="lazy"
            $isLoading={isLoading}
            onLoad={handleSettled}
            onError={handleSettled}
          />
        )}
        {isFromCamera && (
          <Styled.AttachmentCameraBadge
            role="img"
            aria-label={t('noteSource.cameraMedia')}
            data-testid="note-attachment-camera-badge"
          >
            <CctvCameraIcon aria-hidden />
          </Styled.AttachmentCameraBadge>
        )}
        {!isLoading && (
          <Styled.AttachmentOpenIndicator $isPlayIcon={isVideo} aria-hidden>
            {isVideo ? <MediaDevices.Play /> : <Arrow.Expand01 />}
          </Styled.AttachmentOpenIndicator>
        )}
      </Styled.AttachmentTile>
    );
  };

  return (
    <>
      {isSingle ? (
        renderTile(attachments[0])
      ) : (
        <Styled.AttachmentGalleryFrame>
          <Styled.AttachmentGallery
            ref={galleryRef}
            onScroll={updateScrollButtons}
            data-testid="note-attachments-gallery"
          >
            {attachments.map(renderTile)}
          </Styled.AttachmentGallery>
          {canScrollPrevious && (
            <Styled.AttachmentScrollButton
              $size="small"
              $position="previous"
              type="button"
              onClick={handleScrollClick('previous')}
              aria-label={t('note.previousAttachments')}
            >
              <Arrow.ChevronLeft />
            </Styled.AttachmentScrollButton>
          )}
          {canScrollNext && (
            <Styled.AttachmentScrollButton
              $size="small"
              $position="next"
              type="button"
              onClick={handleScrollClick('next')}
              aria-label={t('note.nextAttachments')}
            >
              <Arrow.ChevronRight />
            </Styled.AttachmentScrollButton>
          )}
        </Styled.AttachmentGalleryFrame>
      )}
      {selectedAttachment && (
        <AttachmentModal
          key={selectedAttachment.id}
          noteId={noteId}
          attachment={selectedAttachment}
          open={isAttachmentModalOpen}
          onClose={() => setIsAttachmentModalOpen(false)}
          onDelete={
            canDelete
              ? () => setAttachmentToDelete(selectedAttachment)
              : undefined
          }
        />
      )}
      {attachmentToDelete && (
        <ConfirmDialog
          isOpen
          cancelLabel={t('note.Cancel')}
          successLabel={t('note.Confirm')}
          title={t('note.Are you sure you want to delete this attachment?')}
          successCallback={handleConfirmDelete}
          handleClose={() => setAttachmentToDelete(null)}
        />
      )}
    </>
  );
}
